///////////////////////////////////////////////////////////////////////////////////////
//                          Copyright (c) 2020 - 2025
//                                  RXO Inc.
//                         All Rights Reserved Worldwide
//
//WARNING:  This program (or document) is unpublished, proprietary
//property of RXO Inc.  and is to be maintained in strict confidence.
//Unauthorized reproduction, distribution or disclosure of this program
//(or document), or any program (or document) derived from it is
//prohibited by State and Federal law, and by local law outside of the U.S.
///////////////////////////////////////////////////////////////////////////////////////

import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import AutoComplete from "../form-controls/AutoComplete";
import {
  getOpportunityNames,
  getOpportunity,
  getAccountOpportunityNames
} from "../../api/bids";
import { getShippers, getMainSalesRep } from "../../api/shippers";
import {
  bidOutputFormatsTypes,
  contractOnFileTypes,
  mileageBasisTypes,
  tenderLeadTimeTypes,
  shipperOpsOfficeGroup,
  bidTypes,
  division,
  mode,
  opportunityTypes,
  userRole,
  bidScacType,
  numberOfRoundsOptions,
  operationalCenters as operationalCentersOptions
} from "../../helpers/types";
import OutlinedInput from "../form-controls/OutlinedInput";
import { fetchBidNameExists } from "../../slices/newBidSlice";
import Spinner from "../icons/Spinner";
import ErrorIcon from "../icons/ErrorIcon";
import RefreshIcon from "../icons/RefreshIcon";
import messages from "../../helpers/messages";
import getDefaultOptionForOpportunityName from "../../helpers/getDefaultOptionForOpportunityName";

function General({
  bid: {
    shipper,
    mainSalesRepDetails: {
      mainSalesRep,
      loading: repLoading,
      fetchError: repFetchError,
      noRepErrror
    },
    bidScac,
    rfqName,
    bidOutputFormat,
    contractOnFile,
    mileageBasis,
    tenderLeadTime,
    shipperOpsGroup,
    shipperOpsGroupOffice,
    totalRounds,
    bidType,
    opportunityId,
    modes,
    shipperOpportunityNames,
    salesforceOpportunityId,
    previousSalesforceOpportunityId,
    locationCode,
    foShipperCode,
    operationalCenters,
    freightOptimizerCodes,
    accountOwner,
    additionalBidRounds
  },
  setBidState,
  handleModeSelection,
  setModeDetails,
  validationData,
  removeError,
  validateShipper,
  isBidView,
  initialRfqName = "",
  userOrBidDivision
}) {
  const dispatch = useDispatch();
  const params = useLocation().search;
  const {
    featureFlags: { enableRevvIntegration, useAccountOpportunityNames, crmEnableImdlUi },
    newBid: { step },
    user: {
      user: { internalDivision, role }
    }
  } = useSelector(state => state);
  
  const hasImdlMode = modes?.some(m => m.mode === mode.imdl);

  const showImdlCrmNames = hasImdlMode && bidScac?.value === bidScacType[1].value && crmEnableImdlUi;
  const isRxoNonImdlBid = bidScac?.value === bidScacType[1].value && !hasImdlMode;
  const showCrmLabels = showImdlCrmNames || isRxoNonImdlBid;

  const getFoCodesDisplayValue = (codes) => {
    if (codes && Array.isArray(codes) && codes.length > 0) {
      return codes.join(', ');
    }
    return "";
  };
  
  const [foCodesInputValue, setFoCodesInputValue] = useState(
    getFoCodesDisplayValue(freightOptimizerCodes)
  );

  useEffect(() => {
    setFoCodesInputValue(getFoCodesDisplayValue(freightOptimizerCodes));
  }, [freightOptimizerCodes]);

  const isUserAllowedForBidCreationWithOpportunity =
    internalDivision === division.coy &&
    role !== userRole.tlPricingAnalyst &&
    role !== userRole.ltlPricingAnalyst &&
    role !== userRole.imdlPricingAnalyst &&
    role !== userRole.assetBasedPricingUser &&
    role !== userRole.management &&
    role !== userRole.procurementRepresentative;

  const fetchMainSalesRep = shipperId => {
    removeError("shipper");
    setBidState("mainSalesRepDetails", {
      mainSalesRep: null,
      loading: true,
      noRepErrror: false,
      fetchError: false
    });
    getMainSalesRep(shipperId || shipper.shipperId)
      .then(({ data }) => {
        setBidState("mainSalesRepDetails", {
          mainSalesRep: data.userId ? data : null,
          loading: false,
          noRepErrror: data && data.userId ? false : true,
          fetchError: false
        });
        validateShipper();
      })
      .catch(() => {
        setBidState("mainSalesRepDetails", {
          mainSalesRep: null,
          loading: false,
          noRepErrror: false,
          fetchError: true
        });
        validateShipper();
      });
  };

  const fetchOpportunityNames = shipperId => {
    setBidState("rfqName", "");
    setBidState(
      "shipperOpportunityNames",
      getDefaultOptionForOpportunityName()
    );

    if (useAccountOpportunityNames) {
      getAccountOpportunityNames(shipperId || shipper.shipperId)
        .then(({ data }) => {
          let options = [];

          if (data.opportunityNames?.length > 0) {
            options = data.opportunityNames.map(x => ({
              value: x.id,
              label: x.name
            }));
          } else {
            options.push({
              value: -1,
              label: messages.noResults.defaultMessage
            });
          }

          options.push({
            value: -2,
            label: messages.createOpportunityInRevv.defaultMessage,
            url: `${window.REACT_APP_REVV_WEB_CLIENT_URL}account?id=${data.accountId}&tab=4`
          });

          setBidState("shipperOpportunityNames", options);
        })
        .catch(() => {
          setBidState(
            "shipperOpportunityNames",
            getDefaultOptionForOpportunityName()
          );
        });
    } else {
      getOpportunityNames(shipperId || shipper.shipperId)
        .then(({ data }) => {
          let options = [];

          if (data.length > 0) {
            options = data.map(x => ({
              value: x.id,
              label: x.name
            }));
          } else {
            options.push({
              value: -1,
              label: messages.noResults.defaultMessage
            });
          }

          options.push({
            value: -2,
            label: messages.createOpportunityInRevv.defaultMessage,
            url: window.REACT_APP_REVV_WEB_CLIENT_URL
          });

          setBidState("shipperOpportunityNames", options);
        })
        .catch(() => {
          setBidState(
            "shipperOpportunityNames",
            getDefaultOptionForOpportunityName()
          );
        });
    }
  };

  const handleShipperChange = selectedShipper => {
    setBidState("shipper", selectedShipper);

    if (selectedShipper) {
      fetchMainSalesRep(selectedShipper.shipperId);

      if (enableRevvIntegration && isUserAllowedForBidCreationWithOpportunity) {
        fetchOpportunityNames(selectedShipper.shipperId);
      } else {
        rfqName &&
          dispatch(fetchBidNameExists(rfqName, selectedShipper.shipperId));
      }

    } else {
      setBidState("mainSalesRepDetails", {
        mainSalesRep: null,
        loading: false,
        noRepErrror: false,
        fetchError: false
      });
      if (enableRevvIntegration && isUserAllowedForBidCreationWithOpportunity) {
        setBidState("rfqName", "");
        setBidState(
          "shipperOpportunityNames",
          getDefaultOptionForOpportunityName()
        );
      }
    }
  };

  const validationProps = field => ({
    onFocus: () => removeError(field),
    error: !validationData[field].isValid,
    validationMessage: validationData[field].validationMessage
  });

  const getMainSalesRepValue = () => {
    if (repLoading || repFetchError || noRepErrror) {
      return " ";
    } else if (mainSalesRep && mainSalesRep.fullName) {
      return mainSalesRep.fullName;
    }
    return "";
  };

  const handleRfqNameBlur = () => {
    if (rfqName) {
      const trimmedRfqName = rfqName.trim().replace(/\s\s+/g, " ");
      setBidState("rfqName", trimmedRfqName);

      if (shipper && trimmedRfqName && bidScac.value === bidScacType[0].value) {
        initialRfqName !== trimmedRfqName &&
          dispatch(
            fetchBidNameExists(trimmedRfqName, shipper.shipperId, isBidView)
          );
      } else if (trimmedRfqName && bidScac.value === bidScacType[1].value) {
        initialRfqName !== trimmedRfqName &&
          dispatch(fetchBidNameExists(trimmedRfqName, null, isBidView));
      }
    }
  };

  const handleChange = event => {
    if (
      !event.target.validity.patternMismatch &&
      parseInt(event.target.value, 10) !== parseInt(event.target.min, 10)
    ) {
      setBidState(
        event.target.id,
        event.target.value
          ? parseInt(event.target.value, 10)
          : event.target.value
      );
    }
  };

  const handleTotalRoundsChange = (optionOrEvent) => {
    let newTotalRounds;
    
    if (optionOrEvent.target) {
      const { target } = optionOrEvent;
      if (target.validity.patternMismatch) return;
      newTotalRounds = target.value;
    } else {
      newTotalRounds = optionOrEvent;
    }

    setBidState("totalRounds", newTotalRounds);

    const totalRoundsValue = typeof newTotalRounds === "object" ? newTotalRounds?.value : parseInt(newTotalRounds);
    const expectedAdditionalRounds = totalRoundsValue > 1 ? totalRoundsValue - 1 : 0;
    
    const currentRounds = additionalBidRounds || [];
    
    if (currentRounds.length > expectedAdditionalRounds) {
      const trimmedRounds = currentRounds.slice(0, expectedAdditionalRounds);
      setBidState("additionalBidRounds", trimmedRounds);
    }
  };

  const coyoteFleetBidType = bidTypes.find(
    x => x.label === messages.coyoteFleet.defaultMessage
  ).value;

  const handleBidTypeChange = bidType => {
    setBidState("bidType", bidType);
    if (bidType?.value === coyoteFleetBidType) {
      setBidState("shipperOpsGroup", "");
      setBidState("shipperOpsGroupOffice", null);
    }
  };

  const handleOpportunityChange = opportunity => {
    if (opportunityId === opportunity.value || opportunity.value <= 0) {
      return;
    }

    setBidState("rfqName", opportunity.label);
    setBidState("opportunityId", opportunity.value);
    getOpportunity(opportunity.value).then(({ data }) => {
      const bidTypeValue = parseInt(data.type);
      let bidType = bidTypes.find(x => x.value === bidTypeValue);

      if (
        bidTypeValue === opportunityTypes.project ||
        bidTypeValue === opportunityTypes.laneConversion
      ) {
        bidType = bidTypes.find(x => x.value === opportunityTypes.miniBid);
      }

      bidType && handleBidTypeChange(bidType);

      setBidState("openDateUTC", new Date(data.bidOpenDateUTC).toISOString());
      setBidState("closeDateUTC", new Date(data.bidCloseDateUTC).toISOString());
      setBidState(
        "startDateUTC",
        new Date(data.contractStartDateUTC).toISOString()
      );
      setBidState(
        "endDateUTC",
        new Date(data.contractEndDateUTC).toISOString()
      );

      const opportunityModes = data.modes;

      modes.forEach(x => {
        if (
          !opportunityModes.some(
            opportunityMode => parseInt(opportunityMode.mode) === x.mode
          )
        ) {
          isModeSelected(x.mode) && handleModeSelection(x.mode);
        }
      });

      opportunityModes.forEach(opportunityMode => {
        const modeType = parseInt(opportunityMode.mode);
        const numberOfBusinessDays = 254;
        !isModeSelected(modeType) && handleModeSelection(modeType);
        setModeDetails(modeType, "volume", opportunityMode.totalVolume);
        setModeDetails(
          modeType,
          "expectedLanesPerDay",
          Math.round(opportunityMode.expectedVolume / numberOfBusinessDays)
        );
        setModeDetails(
          modeType,
          "goalLanesPerDay",
          Math.round(opportunityMode.targetVolume / numberOfBusinessDays)
        );
      });
    });
  };

  const isModeSelected = type => {
    return modes.some(x => x.mode === type);
  };

  useEffect(() => {
    if (isUserAllowedForBidCreationWithOpportunity) {
      const searchParams = new URLSearchParams(params);
      const bidName = searchParams.get("bidName");
      const shipperName = searchParams.get("shipperName");
      const opportunityId = parseInt(searchParams.get("opportunityId"));
      const shipperId = parseInt(searchParams.get("shipperId"));

      let selectedShipper = null;

      shipper === null &&
        shipperName &&
        getShippers(shipperName).then(response => {
          const data = response.data.find(x => x.shipperId === shipperId);

          if (data) {
            selectedShipper = {
              ...data,
              label: `${data.name} (${data.code})`,
              value: data.shipperId
            };

            if (selectedShipper) {
              handleShipperChange(selectedShipper);
              bidName &&
                opportunityId &&
                handleOpportunityChange({
                  label: bidName,
                  value: opportunityId
                });
            }
          }
        });
    }
    // eslint-disable-next-line
  }, []);


  return (
    <div className="grid section-grid">
      {!enableRevvIntegration && (
        <div className="grid__cell one-half">
          <OutlinedInput
            data-testid="rfq-name"
            value={rfqName}
            onChange={event => setBidState("rfqName", event.target.value)}
            onBlur={handleRfqNameBlur}
            label={showCrmLabels ? messages.uniqueBidName.defaultMessage : messages.rfqName.defaultMessage}
            maxLength="100"
            {...validationProps("rfqName")}
          />
        </div>
      )}
      {bidScac.value === bidScacType[0].value ? (
        <div data-testid="shippers-container" className="grid__cell one-half">
          <AutoComplete
            fetchFrom={getShippers}
            handleOptionsMapping={options =>
              options.map(option => ({
                ...option,
                label: `${option.name} (${option.code})`,
                value: option.shipperId
              }))
            }
            label={messages.shipperName.defaultMessage}
            value={shipper}
            handleChange={handleShipperChange}
            isDisabled={
              isBidView ||
              (enableRevvIntegration &&
                isUserAllowedForBidCreationWithOpportunity &&
                step === 4)
            }
            {...validationProps("shipper")}
          >
            {repFetchError && (
              <button
                data-testid="refresh-button"
                onClick={() => fetchMainSalesRep(shipper.shipperId)}
              >
                <RefreshIcon
                  className="icon-right"
                  width="14px"
                  height="14px"
                />
              </button>
            )}
          </AutoComplete>
        </div>
      ) : null}
      {enableRevvIntegration &&
        (isUserAllowedForBidCreationWithOpportunity &&
        bidScac.value === bidScacType[0].value ? (
          <div
            data-testid="opportunityName-container"
            className="grid__cell one-half"
          >
            <AutoComplete
              label={
                isBidView
                  ? messages.rfqName.defaultMessage
                  : messages.opportunityName.defaultMessage
              }
              handleChange={opportunity => handleOpportunityChange(opportunity)}
              value={rfqName ? { label: rfqName, value: opportunityId } : null}
              staticOptions={shipperOpportunityNames}
              isDisabled={isBidView || step === 4} // TODO: Will be removed in a different PBI
              isCustomOptionsWithLink={true}
              {...validationProps("rfqName")}
            />
          </div>
        ) : (
          <div className="grid__cell one-half">
            <OutlinedInput
              data-testid="rfq-name"
              value={rfqName}
              onChange={event => setBidState("rfqName", event.target.value)}
              onBlur={handleRfqNameBlur}
              label={showCrmLabels ? messages.uniqueBidName.defaultMessage : messages.rfqName.defaultMessage}
            maxLength="100"
            {...validationProps("rfqName")}
          />
          </div>
        ))}
      {bidScac.value === bidScacType[0].value ? (
        <div
          data-testid="shipper-code-container"
          className="grid__cell one-half"
        >
          <OutlinedInput
            value={shipper ? shipper.code : ""}
            disabled
            label={showImdlCrmNames ? messages.foCodes.defaultMessage : messages.shipperCode.defaultMessage}
          />
        </div>
      ) : null}
      {bidScac.value === bidScacType[0].value ? (
        <div data-testid="main-rep-container" className="grid__cell one-half">
          <OutlinedInput
            disabled
            label={showImdlCrmNames ? messages.opportunityLeader.defaultMessage : messages.mainRep.defaultMessage}
            value={getMainSalesRepValue()}
          >
            {repLoading && (
              <Spinner
                fill="#C9CACC"
                height="14px"
                width="14px"
                className="spinner icon-left"
              />
            )}
            {(repFetchError || noRepErrror) && (
              <ErrorIcon
                fill="#C9CACC"
                height="14px"
                width="14px"
                className="icon-left"
              />
            )}
          </OutlinedInput>
        </div>
      ) : null}
      {bidScac.value === bidScacType[1].value ? (
        <>
          <div
            className="grid__cell one-half"
            data-testid="salesforce-opportunity"
          >
            <OutlinedInput
              data-testid="salesforce-opportunity"
              value={salesforceOpportunityId}
              onChange={event =>
                setBidState(
                  "salesforceOpportunityId",
                  event.target.value.trim().replace(/\s\s+/g, " ")
                )
              }
              label={messages.salesforceOpportunityId.defaultMessage}
              maxLength="100"
              {...validationProps("salesforceOpportunityId")}
            />
          </div>
          <div
            className="grid__cell one-half"
            data-testid="previous-salesforce-opportunity"
          >
            {
              <OutlinedInput
                data-testid="previous-salesforce-opportunity"
                value={previousSalesforceOpportunityId}
                onChange={event =>
                  setBidState(
                    "previousSalesforceOpportunityId",
                    event.target.value.trim().replace(/\s\s+/g, " ")
                  )
                }
                label={
                  messages.previousSalesforceOpportunityIdOptional
                    .defaultMessage
                }
                maxLength="100"
                {...validationProps("previousSalesforceOpportunityId")}
              />
            }
          </div>
          <div className="grid__cell one-half" data-testid="location-code">
            <div style={{ display: 'flex', gap: '10px' }}>
              <div style={{ flex: 1 }}>
                <AutoComplete
                  data-testid="location-code"
                  label={messages.operationalCenter.defaultMessage}
                  value={operationalCenters}
                  handleChange={selectedLocationCodes =>
                    setBidState("operationalCenters", selectedLocationCodes)
                  }
                  isMulti
                  staticOptions={operationalCentersOptions}
                  {...validationProps("locationCode")}
                />
              </div>
              {(!operationalCenters || (Array.isArray(operationalCenters) && operationalCenters.length === 0)) &&
               locationCode &&
               typeof locationCode === 'string' &&
               locationCode.trim() !== "" && (
                <div style={{ flex: 1 }}>
                  <OutlinedInput
                    data-testid="old-location-code"
                    value={locationCode}
                    disabled
                    label={`Old ${messages.operationalCenter.defaultMessage}`}
                    maxLength="100"
                  />
                </div>
              )}
            </div>
          </div>
          <div className="grid__cell one-half" data-testid="fo-shipper-code">
            <OutlinedInput
              data-testid="fo-shipper-code"
              value={foCodesInputValue}
              onChange={event => {
                setFoCodesInputValue(event.target.value);
              }}
              onBlur={() => {
                if (foCodesInputValue) {
                  const codes = Array.from(new Set(
                    foCodesInputValue.split(',').map(code => code.trim()).filter(code => code.length > 0)
                  ));
                  setBidState("freightOptimizerCodes", codes.length > 0 ? codes : null);
                } else {
                  setBidState("freightOptimizerCodes", null);
                }
              }}
              label={messages.foCodes.defaultMessage}
              maxLength="500"
              {...validationProps("foShipperCode")}
            />
          </div>
          <div className="grid__cell one-half" data-testid="account-owner">
            <OutlinedInput
              data-testid="account-owner"
              value={accountOwner}
              onChange={event =>
                setBidState("accountOwner", event.target.value)
              }
              label={messages.opportunityLeaderOptional.defaultMessage}
              maxLength="50"
            />
          </div>
        </>
      ) : null}
      <div data-testid="bidType-container" className="grid__cell one-half">
        <AutoComplete
          label={messages.bidType.defaultMessage}
          handleChange={bidType => handleBidTypeChange(bidType)}
          value={bidType}
          staticOptions={
            (!isBidView ||
              (isBidView && bidType?.value === coyoteFleetBidType)) &&
            bidScac.value === bidScacType[0].value
              ? bidTypes
              : bidTypes.filter(
                  x => x.label !== messages.coyoteFleet.defaultMessage
                )
          }
          isDisabled={isBidView && bidType?.value === coyoteFleetBidType}
          {...validationProps("bidType")}
        />
      </div>
      <div data-testid="total-rounds-container" className="grid__cell one-half">
        {bidScac.value === bidScacType[1].value ? (
          <AutoComplete
            data-testid="totalRounds"
            value={totalRounds}
            label={messages.numberOfRounds.defaultMessage}
            staticOptions={numberOfRoundsOptions}
            handleChange={handleTotalRoundsChange}
            {...validationProps("totalRounds")}
          />
        ) : (
          <OutlinedInput
            data-testid="totalRounds"
            value={typeof totalRounds === "object" ? totalRounds?.value : totalRounds}
            type="text"
            label={messages.numberOfRounds.defaultMessage}
            pattern="^[1-9]\d{0,1}$"
            onChange={handleTotalRoundsChange}
            {...validationProps("totalRounds")}
          />
        )}
      </div>
      {bidScac.value === bidScacType[0].value && !(crmEnableImdlUi && hasImdlMode) ? (
        <div data-testid="o-group-container" className="grid__cell one-half">
          <OutlinedInput
            id="shipperOpsGroup"
            value={shipperOpsGroup}
            label={messages.oGroupOptional.defaultMessage}
            pattern="[0-9]*"
            min="0"
            maxLength="3"
            onChange={handleChange}
            disabled={bidType?.value === coyoteFleetBidType}
          />
        </div>
      ) : null}
      {bidScac.value === bidScacType[0].value && !(crmEnableImdlUi && hasImdlMode) ? (
        <div
          data-testid="o-group-office-container"
          className="grid__cell one-half"
        >
          <AutoComplete
            label={messages.oGroupOfficeOptional.defaultMessage}
            handleChange={selectedOGroupOffice =>
              setBidState("shipperOpsGroupOffice", selectedOGroupOffice)
            }
            value={shipperOpsGroupOffice}
            staticOptions={shipperOpsOfficeGroup}
            isDisabled={bidType?.value === coyoteFleetBidType}
          />
        </div>
      ) : null}
      <div data-testid="bid-format-container" className="grid__cell one-half">
        <AutoComplete
          label={messages.bidOutputFormats.defaultMessage}
          value={bidOutputFormat}
          handleChange={selectedBidOutputFormat =>
            setBidState("bidOutputFormat", selectedBidOutputFormat)
          }
          isMulti
          staticOptions={bidOutputFormatsTypes}
          {...validationProps("bidOutputFormat")}
          {...(crmEnableImdlUi && hasImdlMode ? { error: false, validationMessage: "" } : {})}
        />
      </div>
      <div
        data-testid="contract-on-file-container"
        className="grid__cell one-half"
      >
        <AutoComplete
          label={showCrmLabels ? messages.customerStatusOptional.defaultMessage : messages.executedContractOnFileOptional.defaultMessage}
          value={contractOnFile}
          staticOptions={contractOnFileTypes}
          handleChange={selectedContractOnFile =>
            setBidState("contractOnFile", selectedContractOnFile)
          }
        />
      </div>
      <div data-testid="lead-time-container" className="grid__cell one-half">
        <AutoComplete
          label={messages.localTenderLeadTimeOptional.defaultMessage}
          value={tenderLeadTime}
          staticOptions={tenderLeadTimeTypes}
          handleChange={selectedMileageBasis =>
            setBidState("tenderLeadTime", selectedMileageBasis)
          }
        />
      </div>
      <div
        data-testid="mileage-basis-container"
        className="grid__cell one-half"
      >
        <AutoComplete
          label={messages.distanceBasisOptional.defaultMessage}
          value={mileageBasis}
          staticOptions={mileageBasisTypes.filter(
            ({ division }) => !division || division === userOrBidDivision
          )}
          handleChange={selectedMileageBasis =>
            setBidState("mileageBasis", selectedMileageBasis)
          }
        />
      </div>
      {bidScac.value === bidScacType[0].value ? (
        <div
          className="grid__cell one-half"
          data-testid="salesforce-opportunity"
        >
          {
            <OutlinedInput
              data-testid="salesforce-opportunity"
              value={salesforceOpportunityId}
              onChange={event =>
                setBidState(
                  "salesforceOpportunityId",
                  event.target.value.trim().replace(/\s\s+/g, " ")
                )
              }
              label={messages.salesforceOpportunityId.defaultMessage}
              maxLength="100"
              {...validationProps("salesforceOpportunityId")}
            />
          }
        </div>
      ) : null}
    </div>
  );
}

export default General;
